import { useContext, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useLazyQuery, useQuery } from "@apollo/client";

import { USER_CREATED_RECIPE } from "../../app/constants/constants";
import { GET_MOBILE_CREATED_RECIPE, GET_RECIPE_E3_FROM_ID, GET_RECIPE_FROM_ID, GET_RECIPE_INGREDIENTS } from "../../app/graphql/queries";
import { UseGetFlag } from "../../app/launchDarkly";
import { setOldRecipeData, setRecipeData } from "../../app/recipeCreation/createRecipe.slice";
import { setRecipe } from "../../app/recipePage/recipePage.slice";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks";
import { ErrorManagementContext } from "../errorManagement/ErrorManagementProvider";
import {
  recipeE3TransformerQL2FS,
  recipeTransformerQL2FS,
} from "./hasuraFSMigrationTransformers";
import { setIngredientsList } from "../../app/homepage/homepage.slice";

const useRecipeFromDB = (recipeId: string) => {
  const { cookClubLog } = useContext(ErrorManagementContext);
  const recipe = useAppSelector((state) => state.recipePage.recipe)
  const [recipeJSON, setRecipeJSON] = useState<any>(null)
  const { enableExperience3 } = UseGetFlag()
  const dispatch = useAppDispatch()
  const { type } = useParams<any>();
  const query = type === USER_CREATED_RECIPE ? GET_MOBILE_CREATED_RECIPE : GET_RECIPE_E3_FROM_ID;
  const history = useHistory();
  const [fetchRecipeIngredients, { loading: fetchingIngredients }] = useLazyQuery(GET_RECIPE_INGREDIENTS)
  useQuery(GET_RECIPE_FROM_ID, {
    variables: { Recipe_ID: recipeId },
    skip: enableExperience3,
    onCompleted: (data) => setRecipeJSON(recipeTransformerQL2FS(data.Recipe_DB_by_pk)),
    onError: (err) =>
      cookClubLog(
        `Fetching recipeId ${recipeId}`,
        "ERROR_ON_ACCESS_RECIPE",
        { recipeId, err },
        { ui: { type: "error" }, browserConsole: true }
      ),
  });

  const { loading: fetchingRecipe } = useQuery(query, {
    variables: { id: recipeId },
    skip: !enableExperience3,
    onCompleted: async (data) => {
      const transformableData = type === USER_CREATED_RECIPE ? data?.User_Generated_Recipes[0]?.recipeData : data.Recipes_E3_by_pk
      if (type === USER_CREATED_RECIPE) {
        dispatch(setRecipeData(transformableData));
        dispatch(setRecipe({ recipe: recipeE3TransformerQL2FS(transformableData) }));
      }
      else {
        await fetchRecipeIngredients({
          variables: { _in: transformableData?.Recipe_Variations[0]?.ingredients }
        }).then(({ data: fetchedRecipeIngredientsData }) => {
          if (fetchedRecipeIngredientsData?.Ingredients_DB) {
            dispatch(setIngredientsList(fetchedRecipeIngredientsData.Ingredients_DB ?? []))
          }
        })
        dispatch(setRecipe({ recipe: recipeE3TransformerQL2FS(transformableData) }))
        dispatch(setOldRecipeData(data.Recipes_E3_by_pk))
      }
    },
    onError: (err) => {
      cookClubLog(
        `Fetching recipeId ${recipeId}`,
        "ERROR_ON_ACCESS_RECIPE",
        { recipeId, err },
        { ui: { type: "error" }, browserConsole: true }
      );
      history.push("/");
    }
  });

  const loading = useMemo(() => (recipe?.id !== recipeId) && !recipeJSON, [recipe, recipeJSON])

  return { recipe: recipeJSON, loading: loading || fetchingRecipe || fetchingIngredients };
};

export default useRecipeFromDB;
